import React from 'react'
import { Placeholder } from 'semantic-ui-react'
import classnames from 'classnames/bind'
import type { ReoOffer, TrusteeContract } from '@adc/sdk/types'
import { closing } from '@adc/blueprint'
import {
  GRAPH_API_FRAGMENT_DEFINITION,
  GRAPH_API_FRAGMENT_REFERENCE,
} from '../../../utils/symbols'
import {
  AddressInfoLine,
  CardImage,
  ListingCardIndicator,
} from '../../listing/listingCard/cardParts'
import SaleInfo from '../saleInfo'
import styles from './styles.scss'
import { SlottableComponent } from '../../../types'

const cx = classnames.bind(styles)

const renderPlaceholder = () => (
  <div className={cx('property-value-container')}>
    <Placeholder className={cx('placeholder-main')}>
      <Placeholder.Header>
        <Placeholder.Line length='short' />
      </Placeholder.Header>
      <Placeholder.Paragraph style={{ width: '100%' }}>
        <Placeholder.Line />
        <Placeholder.Line />
        <Placeholder.Line />
        <Placeholder.Line />
      </Placeholder.Paragraph>
    </Placeholder>
  </div>
)

interface ClosingCardProps extends SlottableComponent<'action' | 'details'> {
  className?: string
  elmId?: string
  closingMethod: ReoOffer | TrusteeContract
  loading?: boolean
  disabled?: boolean
  featureFlags: object
}

const ClosingCard = ({
  className = '',
  elmId = 'listing_card',
  closingMethod,
  loading = false,
  disabled = false,
  slots,
  featureFlags,
}: ClosingCardProps) => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const listing = closingMethod.listing

  let soldOn: string | null = null
  if (closingMethod.__typename === 'TrusteeContract') {
    soldOn =
      closingMethod.created_date != null
        ? String(closingMethod.created_date)
        : null
  } else if (closingMethod.__typename === 'ReoOffer') {
    soldOn =
      closingMethod.sold_time != null ? String(closingMethod.sold_time) : null
  }

  return (
    <div className={cx('root', { 'root-card-disabled': disabled }, className)}>
      <div
        className={cx('base-card', className)}
        data-elm-id={`property_card_${elmId}`}
      >
        <CardImage
          // @ts-ignore not typed yet
          listing={listing}
          elmId={elmId}
          showImageCount={false}
          featureFlags={featureFlags}
          loading={loading}
          maxBadges={0}
          className={cx('card-image')}
        />

        <div className={cx('property-card-body')}>
          <div className={cx('card-cta', 'desktop')}>{slots?.action?.()}</div>
          {loading ? (
            renderPlaceholder()
          ) : (
            <>
              <SaleInfo
                listingStatusGroup={listing.listing_status_group}
                soldAmount={closingMethod.winning_bid_amount}
                soldOn={soldOn}
                elmId={`${elmId}_sold_value`}
              />
              <AddressInfoLine
                // @ts-ignore next
                address={listing.formatted_address || undefined}
                elmId={`${elmId}_address`}
                isCancelled={disabled}
                className='u-mb-1'
              />

              <ListingCardIndicator
                listing={listing}
                elmId={`${elmId}_listing_indicator`}
                className={cx('asset-info')}
              />
            </>
          )}
          <div className={cx('card-cta')}>{slots?.action?.()}</div>
        </div>
      </div>
      <div>{slots?.details?.()}</div>
    </div>
  )
}

// Fragments sourced from @adc/blueprint (centralized). See CLOSE-266.
const { closingListing, reoOfferList, trusteeContractList } = closing.fragments

/**
 * This is a little different than our usual pattern as we have multiple fragments.
 * ReoOffer/TrusteeContract fragments spread the shared closingListing fragment,
 * so consumers must include ContractListingFragment alongside them.
 */
ClosingCard.ContractListingFragment = {
  [GRAPH_API_FRAGMENT_DEFINITION]: closingListing.definition,
  [GRAPH_API_FRAGMENT_REFERENCE]: closingListing.reference,
}

ClosingCard.TrusteeContractFragment = {
  [GRAPH_API_FRAGMENT_DEFINITION]: trusteeContractList.definition,
  [GRAPH_API_FRAGMENT_REFERENCE]: trusteeContractList.reference,
}

ClosingCard.ReoOfferFragment = {
  [GRAPH_API_FRAGMENT_DEFINITION]: reoOfferList.definition,
  [GRAPH_API_FRAGMENT_REFERENCE]: reoOfferList.reference,
}

export default ClosingCard
